// Column sizing for Table: a grammar instead of per-cell widths.
//
// A table that declares its columns fills the box it sits in, and each
// column takes the share its job warrants. Four kinds:
//
//   control      a checkbox, a row menu: as wide as its control, never more
//   primary      the column that names the row: takes the room left over
//                first, gives it back first, and can truncate
//   content      ordinary facts (people, dates with notes): a sensible
//                minimum, then a share of any spare room
//   constrained  short, predictable values (a score, a status, a date):
//                given the width they need, growing at most to their max
//
// Sizes are content widths in px, without the cell's padding: Table adds
// the padding of the density it is drawn at, so a column definition holds
// at either density. Where a column gives no minimum, its content does:
// a control column and a constrained column without `min` are measured
// (their widest cell, on one line), and every column is at least as wide
// as its header label, so headers never truncate.
//
// Below the sum of the minimums the table stops shrinking and its
// container scrolls sideways.

export type TableColumnKind = 'control' | 'primary' | 'content' | 'constrained';

export interface TableColumn {
  /** Matches the `column` prop of this column's cells. */
  id: string;
  kind: TableColumnKind;
  /** A fixed content width: the column neither grows nor shrinks. */
  width?: number;
  /** The narrowest the content gets before the table scrolls sideways. */
  min?: number;
  /** The width it takes when every column has room for its ideal. */
  ideal?: number;
  /** The widest it grows. Constrained columns stop at their ideal unless given one. */
  max?: number;
  /** `end` for numbers, so they line up (tabular figures); `center` for a mark. */
  align?: 'start' | 'center' | 'end';
  /** One line per block, ending in an ellipsis, instead of wrapping. */
  truncate?: boolean;
  /** Stays in place when the table scrolls sideways. Leading columns only. */
  pin?: boolean;
}

/** What Table measures in the browser for each column. */
export interface ColumnMeasure {
  /** The cell's horizontal padding, left plus right. */
  padding: number;
  /** The header label's width on one line. */
  header: number;
  /** The widest body cell on one line, for the columns sized by their content. */
  content: number;
}

/** A column's resolved limits, as full widths (padding included). */
export interface ColumnTrack {
  kind: TableColumnKind;
  min: number;
  basis: number;
  max: number;
  grow: number;
}

// Fallback minimums for a column that gives none. Not page measurements:
// a primary name needs room for a few words, a fact for a short phrase.
const FALLBACK_MIN: Record<Exclude<TableColumnKind, 'control'>, number> = {
  primary: 200,
  content: 96,
  constrained: 64,
};

// How spare room is shared: the primary column takes three parts to every
// other flexible column's one. Controls never grow.
const GROW: Record<TableColumnKind, number> = { control: 0, constrained: 1, content: 1, primary: 3 };

// Which columns give room back first when there is not enough.
const SHRINK_ORDER: TableColumnKind[] = ['primary', 'content', 'constrained'];

/** Measured content sizes only need the kinds that are sized by content. */
export function isMeasured(column: TableColumn): boolean {
  return column.width === undefined && (column.kind === 'control' || (column.kind === 'constrained' && column.min === undefined));
}

export function resolveTrack(column: TableColumn, m: ColumnMeasure): ColumnTrack {
  const pad = m.padding;
  if (column.width !== undefined || column.kind === 'control') {
    const w = Math.ceil(Math.max(column.width ?? Math.max(m.content, m.header), 0)) + pad;
    return { kind: column.kind, min: w, basis: w, max: w, grow: 0 };
  }
  const contentMin =
    column.min ?? (column.kind === 'constrained' && m.content > 0 ? m.content : FALLBACK_MIN[column.kind]);
  const min = Math.ceil(Math.max(contentMin, m.header)) + pad;
  const basis = Math.max(min, Math.ceil(column.ideal ?? 0) + pad);
  const ceiling = column.max ?? (column.kind === 'constrained' ? column.ideal ?? contentMin : Infinity);
  const max = Math.max(basis, ceiling === Infinity ? Infinity : Math.ceil(ceiling) + pad);
  return { kind: column.kind, min, basis, max, grow: max > basis ? GROW[column.kind] : 0 };
}

/**
 * Shares `available` px between the tracks: every track at its basis,
 * then spare room by grow weight up to each max, or a shortfall taken from
 * the primary column first, then content, then constrained, down to their
 * minimums. Returns whole-pixel widths. Their sum is `available`, or the
 * sum of the minimums when that is wider (the table then scrolls).
 */
export function distributeColumnWidths(tracks: ColumnTrack[], available: number): number[] {
  const w = tracks.map((t) => t.basis);
  const total = w.reduce((a, b) => a + b, 0);

  if (available < total) {
    let deficit = total - available;
    for (const kind of SHRINK_ORDER) {
      if (deficit <= 0) break;
      const group = tracks.map((_, i) => i).filter((i) => tracks[i].kind === kind && w[i] > tracks[i].min);
      const room = group.reduce((a, i) => a + (w[i] - tracks[i].min), 0);
      if (room <= 0) continue;
      const take = Math.min(deficit, room);
      for (const i of group) w[i] -= (take * (w[i] - tracks[i].min)) / room;
      deficit -= take;
    }
  } else if (available > total) {
    let extra = available - total;
    let open = tracks.map((_, i) => i).filter((i) => tracks[i].grow > 0 && w[i] < tracks[i].max);
    while (extra > 0.01 && open.length) {
      const weight = open.reduce((a, i) => a + tracks[i].grow, 0);
      let used = 0;
      for (const i of open) {
        const add = Math.min((extra * tracks[i].grow) / weight, tracks[i].max - w[i]);
        w[i] += add;
        used += add;
      }
      extra -= used;
      open = open.filter((i) => w[i] < tracks[i].max - 0.01);
      if (used < 0.01) break;
    }
  }

  // Whole pixels, so pinned offsets land on the same edges as the cells.
  // The remainder goes to the primary column, or the last flexible one.
  // If every column has reached its max, the table stops growing.
  const whole = w.map((x) => Math.floor(x));
  const target = Math.max(Math.round(available), tracks.reduce((a, t) => a + t.min, 0));
  const rest = target - whole.reduce((a, b) => a + b, 0);
  if (rest > 0) {
    const primary = tracks.findIndex((t) => t.kind === 'primary');
    const flexible = tracks.map((_, i) => i).filter((i) => tracks[i].kind !== 'control' && whole[i] < tracks[i].max);
    const at = primary >= 0 ? primary : flexible[flexible.length - 1];
    if (at !== undefined) whole[at] += Math.min(rest, tracks[at].max - whole[at]);
  }
  return whole;
}

/** The leading run of pinned columns: a pin after an unpinned column has no effect. */
export function pinnedCount(columns: TableColumn[]): number {
  let n = 0;
  while (n < columns.length && columns[n].pin) n++;
  return n;
}
